import React, { useState, useEffect } from 'react'
import {
  UserPlus,
  Edit2,
  Trash2,
  Shield,
  ShieldOff,
  Check,
  Copy,
} from 'lucide-react'
import AddManagerModal from '../modals/AddManagerModal'
import EditManagerModal from '../modals/EditManagerModal'
import managerService from '../../services/managerService'
import apiService from '../../services/apiService'
import authService from '../../services/authService'
import './ManagersList.scss'
import { db } from '../../database/db'

const ManagersList = () => {
  const [managers, setManagers] = useState([])
  const [loading, setLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingManager, setEditingManager] = useState(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    loadManagers()
  }, [])

  const loadManagers = async () => {
    setLoading(true)
    const connected = await authService.isOnline()

    if (connected) {
      const token = authService.getToken()
      const response = await apiService.getManagers(token)

      if (response.success) {
        setManagers(response.data)

        // mise a jour local
        for (const manager of response.data) {
          const oneManager = await db.managers
            .where('_id')
            .equals(manager._id)
            .first()
          if (!oneManager) {
            await managerService.addManagerLocal(manager)
          } else {
            await db.managers.update(oneManager.id, {
              ...oneManager,
              updatedAt: new Date().toISOString(),
            })
          }
        }

        // for (const manager of response.data) {
        //   await managerService.deleteManagerLocal(manager._id)
        // }
        // for (const manager of response.data) {
        //   await managerService.addManagerLocal(manager)
        // }
      }
    } else {
      const localManagers = await managerService.getManagersLocal()
      setManagers(localManagers)
    }

    setLoading(false)
  }

  const handleAddManager = async (managerData) => {
    const connected = await authService.isOnline()

    if (!connected) {
      alert('Connexion internet requise pour ajouter un gérant')
      return
    }

    setLoading(true)
    const token = authService.getToken()
    const response = await apiService.addManager(token, managerData)

    if (response.success) {
      await managerService.addManagerLocal(response.data)
      await loadManagers()
      setShowAddModal(false)
      alert('Gérant ajouté avec succès')
    } else {
      alert(response.error || "Erreur lors de l'ajout")
    }

    setLoading(false)
  }

  const handleEditClick = (manager) => {
    setEditingManager(manager)
    setShowEditModal(true)
  }

  const handleUpdateManager = async (managerId, managerData) => {
    const connected = await authService.isOnline()

    if (!connected) {
      alert('Connexion internet requise pour modifier un gérant')
      return
    }

    setLoading(true)
    const token = authService.getToken()
    const response = await apiService.updateManager(
      token,
      managerId,
      managerData,
    )

    if (response.success) {
      await managerService.updateManagerLocal(managerId, response.data)
      await loadManagers()
      setShowEditModal(false)
      setEditingManager(null)
      alert('Gérant modifié avec succès')
    } else {
      alert(response.error || 'Erreur lors de la modification')
    }

    setLoading(false)
  }

  const handleDeleteManager = async (managerId) => {
    const confirmed = window.confirm(
      'Êtes-vous sûr de vouloir supprimer ce gérant ? Il ne pourra plus se connecter.',
    )
    if (!confirmed) return

    const connected = await authService.isOnline()
    if (!connected) {
      alert('Connexion internet requise pour supprimer un gérant')
      return
    }

    setLoading(true)
    const token = authService.getToken()
    const response = await apiService.deleteManager(token, managerId)

    if (response.success) {
      await managerService.deleteManagerLocal(managerId)
      await loadManagers()
      alert('Gérant supprimé avec succès')
    } else {
      alert(response.error || 'Erreur lors de la suppression')
    }

    setLoading(false)
  }

  const getPermissionsSummary = (permissions) => {
    const granted = Object.entries(permissions).filter(
      ([_, value]) => value,
    ).length
    const total = Object.keys(permissions).length
    return `${granted}/${total} permissions`
  }

  const handleCopyLink = () => {
    if (managers.length === 0) return

    const link = `${window.location.protocol}//${window.location.host}/manager/?shopId=${managers[0].shopId}`

    navigator.clipboard
      .writeText(link)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch((err) => {
        console.error('Erreur copie:', err)
        alert('Erreur lors de la copie du lien')
      })
  }

  const currentRole = managerService.getCurrentManagerRole()
  const isOwner = currentRole === 'owner'

  if (!isOwner) {
    return (
      <div className='card'>
        <p className='text-center text-gray'>
          Accès réservé au propriétaire de la boutique
        </p>
      </div>
    )
  }

  return (
    <div className='card managers-section'>
      <div className='section-header'>
        <div>
          <h3>Gestion des Gérants</h3>
          <p className='section-subtitle'>
            {managers.length} gérant{managers.length > 1 ? 's' : ''} enregistré
            {managers.length > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className='btn primary'
          disabled={loading}
        >
          <UserPlus size={16} />
          Ajouter un gérant
        </button>
      </div>

      {managers.length > 0 && (
        <div className='manager-login-link'>
          <div className='link-label'>Lien de connexion gérant :</div>
          <div className='link-container'>
            <div className='link-text'>
              {`${window.location.protocol}//${window.location.host}/manager/?shopId=${managers[0].shopId}`}
            </div>
            <button
              onClick={handleCopyLink}
              className={`copy-button ${copied ? 'copied' : ''}`}
              title='Copier le lien'
            >
              {copied ? (
                <span>
                  Copié <Check size={16} />
                </span>
              ) : (
                <span>
                  Copier <Copy size={16} />
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {loading && managers.length === 0 ? (
        <div className='loading-state'>
          <p>Chargement des gérants...</p>
        </div>
      ) : managers.length === 0 ? (
        <div className='empty-state'>
          <Shield size={48} className='empty-icon' />
          <h4>Aucun gérant enregistré</h4>
          <p>Ajoutez des gérants pour déléguer la gestion de votre boutique</p>
        </div>
      ) : (
        <div className='managers-list'>
          {managers.map((manager) => (
            <div key={manager._id} className='manager-card'>
              <div className='manager-info'>
                <div className={`manager-avatar ${manager.role}`}>
                  {manager.role === 'owner' ? (
                    <Shield size={24} />
                  ) : (
                    <ShieldOff size={24} />
                  )}
                </div>
                <div className='manager-details'>
                  <h4>{manager.name}</h4>
                  <p className='manager-phone'>{manager.phone}</p>
                  <div className='manager-meta'>
                    <span className={`role-badge ${manager.role}`}>
                      {manager.role === 'owner' ? 'Propriétaire' : 'Gérant'}
                    </span>
                    {manager.role === 'manager' && (
                      <span className='permissions-badge'>
                        {getPermissionsSummary(manager.permissions)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {manager.role !== 'owner' && (
                <div className='manager-actions'>
                  <button
                    onClick={() => handleEditClick(manager)}
                    className='btn-icon edit'
                    disabled={loading}
                    title='Modifier le gérant'
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteManager(manager._id)}
                    className='btn-icon danger'
                    disabled={loading}
                    title='Supprimer le gérant'
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <AddManagerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddManager}
        loading={loading}
      />

      <EditManagerModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setEditingManager(null)
        }}
        manager={editingManager}
        onUpdate={handleUpdateManager}
        loading={loading}
      />
    </div>
  )
}

export default ManagersList
